import {DashboardUserInterface} from "../interfaces/dashboard.user.interface";
import {DatatableResponseInterface} from "../interfaces/datatable.response.interface";

interface DashboardUserApiResponse {
  id?: string | number;
  userId?: string | number;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  userMetadata?: string | null;
  skipReleaseNotification?: boolean;
  isWorkOsMigrated?: boolean;
  managementCompanyId?: number | null;
  isOnboardUser?: boolean | null;
  dashboardUser?: boolean | null;
  fullAccessProfile?: boolean | null;
  reportsUser?: boolean | null;
  userType?: 'Internal' | 'External';
  readOnly?: boolean | null;
}

const parseUserMetadata = (userMetadata?: string | null) => {
  if (!userMetadata) {
    return null;
  }

  try {
    return JSON.parse(userMetadata);
  } catch {
    return null;
  }
};

const getNameParts = (user: DashboardUserApiResponse) => {
  if (user.firstName || user.lastName) {
    return {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
    };
  }

  const trimmedName = user.name?.trim();
  if (trimmedName) {
    const [firstName, ...rest] = trimmedName.split(" ").filter(Boolean);
    return {
      firstName: firstName || "",
      lastName: rest.join(" "),
    };
  }

  const emailPrefix = user.email?.split("@")[0] || "";
  return {
    firstName: emailPrefix,
    lastName: "",
  };
};

const buildUserMetadata = (user: DashboardUserApiResponse) => {
  const parsedMetadata = parseUserMetadata(user.userMetadata);
  const companyId = parsedMetadata?.companyId ?? user.managementCompanyId ?? 0;
  const onboardUser = parsedMetadata?.onboardUser ?? user.isOnboardUser ?? false;
  const dashboardUser = parsedMetadata?.dashboardUser ?? user.dashboardUser ?? false;
  const fullAccessProfile = parsedMetadata?.fullAccessProfile ?? user.fullAccessProfile ?? false;
  const reportsUser = parsedMetadata?.reportsUser ?? user.reportsUser ?? false;

  return JSON.stringify({
    companyId,
    onboardUser,
    dashboardUser,
    fullAccessProfile,
    reportsUser,
  });
};

export const normalizeDashboardUser = (user: DashboardUserApiResponse): DashboardUserInterface => {
  const normalizedUserId = String(user.userId ?? user.id ?? "");
  const {firstName, lastName} = getNameParts(user);

  return {
    id: String(user.id ?? normalizedUserId),
    email: user.email || "",
    name: user.name || `${firstName} ${lastName}`.trim(),
    userId: normalizedUserId,
    userMetadata: buildUserMetadata(user),
    skipReleaseNotification: Boolean(user.skipReleaseNotification),
    isWorkOsMigrated: user.isWorkOsMigrated,
    managementCompanyId: user.managementCompanyId ?? undefined,
    firstName,
    lastName,
    isOnboardUser: Boolean(user.isOnboardUser),
    dashboardUser: Boolean(user.dashboardUser),
    fullAccessProfile: Boolean(user.fullAccessProfile),
    reportsUser: Boolean(user.reportsUser),
    userType: user.userType,
    readOnly: user.readOnly ?? null,
  };
};

export const normalizeDashboardUsersResponse = (
  response: DatatableResponseInterface
): DatatableResponseInterface => {
  return {
    ...response,
    items: (response.items || []).map((item) => normalizeDashboardUser(item as DashboardUserApiResponse)),
  };
};
